import {
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  makeStyles,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import {
  Close,
  GetApp,
  Videocam,
  VideocamOff,
  SwitchVideo,
} from '@material-ui/icons'
import { Form } from 'react-final-form'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  sanitizeFetchType,
  useMutation,
  useNotify,
  useShowContext,
  useTranslate,
} from 'react-admin'
import ReactHlsPlayer from 'react-hls-player/dist'
import Popout from 'react-popout'
import { TypeSocket } from 'typesocket'
import { Authority } from '../../../../core/auth/Authority'
import { DateTime } from '../../../../core/common/date-time'
import { CameraDto } from '../../../../core/dto/device/camera/cameras/camera.dto'
import { CameraTypes } from '../../../../core/enum/CameraTypes'
import { ResourceName } from '../../../../core/ResourceName'
import Button, {
  ButtonProps,
} from '../../../common/customized-mui-components/Button'
import DraggableComponent from '../../../common/DraggableComponent'
import { useHasAuthority } from '../../../hooks/useHasAuthority'
import FilteredReferenceInput from '../../../common/FilteredReferenceInput'

const useStyles = makeStyles(() =>
  createStyles({
    actions: {
      padding: 0,
      background: 'rgba(0,0,0,0.2)',
    },
    title: {
      paddingLeft: '10px',
      width: '100%',
    },
    button: {
      margin: '2px',
    },
    video: {
      maxWidth: '-webkit-fill-available',
      maxHeight: '500px',
    },
    dialogContent: {
      padding: 0,
    },
    cameraSelect: {
      position: 'absolute',
      opacity: 0,
      pointerEvents: 'none',
      '& > div:first-child': {
        backgroundColor: 'inherit',
        '& > div:first-child': {
          padding: 0,
        },
      },
    },
  }),
)

const CameraPreviewDialog = ({
  open,
  close,
  startDate,
  cameraData,
  logId,
}: {
  open: boolean
  close: () => void
  startDate?: DateTime
  cameraData?: CameraPreviewData
  logId?: number
}) => {
  let camera: CameraDto | CameraPreviewData | undefined
  const { record } = useShowContext<CameraDto>()
  if (cameraData) camera = cameraData
  else camera = record
  const playerRef = React.useRef<HTMLVideoElement>(null)
  const [jobId, setJobId] = useState<number | undefined>(undefined)
  const [streamPath, setStreamPath] = useState('')
  const [showHLSPlayer, setShowHLSPlayer] = useState(true)
  const [hlsStreamLoaded, setHLSStreamLoaded] = useState(false)
  const [maxHLSRetriesLeft, setMaxHLSRetriesLeft] = useState(30)
  const classes = useStyles()
  const notify = useNotify()
  const translate = useTranslate()
  const [mutate, { loading: mutationLoading }] = useMutation()
  const [cameraDescription, setCameraDescription] = useState<string>()
  const [cameraType, setCameraType] = useState<CameraTypes | undefined>()

  // Common
  const [openStream] = useMutation(
    {
      type: 'openStream',
      resource: ResourceName.CAMERAS,
      payload: {
        cameraId: camera?.id,
        streamStart: startDate,
        logId,
      },
    },
    {
      onSuccess: ({ data }) => {
        setCameraDescription(data.description)
        setCameraType(data.serverType)
        setStreamPath(data.streamPath)
        setJobId(data.jobId)
      },
    },
  )
  const [doCloseStream] = useMutation({
    type: 'deleteJob',
    resource: ResourceName.JOBS,
    payload: {
      jobId,
    },
  })
  const closeStream = useCallback(async () => {
    if (jobId) doCloseStream()
  }, [jobId, doCloseStream])
  const handleClose = useCallback(() => {
    close()
  }, [close])

  // Downloading clips
  const handleDownloadClip = async () => {
    const {
      data: {
        blob: { body, headers },
      },
    } = await mutate(
      {
        type: sanitizeFetchType('downloadClip'),
        resource: ResourceName.CAMERAS,
        payload: {
          cameraId: camera?.id,
          streamStart: startDate,
        },
      },
      {
        returnPromise: true,
        onFailure: (msg) => notify(msg, 'error'),
      },
    )

    const fileName = decodeURIComponent(
      headers?.get('Content-Disposition')?.split("filename*=UTF-8''")[1],
    )

    const objectUrl: string = URL.createObjectURL(body)
    const a: HTMLAnchorElement = document.createElement(
      'a',
    ) as HTMLAnchorElement

    a.href = objectUrl
    a.download = fileName
    document.body.appendChild(a)
    a.click()

    document.body.removeChild(a)
    URL.revokeObjectURL(objectUrl)
  }

  // RTSP
  const reloadHLSPlayer = () => {
    setShowHLSPlayer(false)
  }
  const onHlsDataLoaded = () => {
    setHLSStreamLoaded(true)
  }
  const handleHLSTimeout = useCallback(() => {
    handleClose()
    notify('resources.cameras.dialogs.preview.streamTimeout')
  }, [handleClose, notify])
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (
      cameraType === CameraTypes.VDG_RTSP ||
      CameraTypes.HIKVISION_RTSP ||
      CameraTypes.DAHUA_RTSP ||
      CameraTypes.GENETEC_RTSP ||
      CameraTypes.BOSCH_RTSP
    ) {
      const interval = setInterval(() => {
        reloadHLSPlayer()
        setMaxHLSRetriesLeft(maxHLSRetriesLeft - 1)
      }, 1300)
      if (hlsStreamLoaded || !open) {
        clearInterval(interval)
      } else if (maxHLSRetriesLeft <= 0) {
        handleHLSTimeout()
      }
      return () => clearInterval(interval)
    }
  }, [hlsStreamLoaded, open, maxHLSRetriesLeft, handleHLSTimeout, cameraType])
  useEffect(() => {
    if (
      (cameraType === CameraTypes.VDG_RTSP ||
        CameraTypes.HIKVISION_RTSP ||
        CameraTypes.DAHUA_RTSP ||
        CameraTypes.GENETEC_RTSP ||
        CameraTypes.BOSCH_RTSP) &&
      !showHLSPlayer
    )
      setShowHLSPlayer(true)
  }, [showHLSPlayer, cameraType])
  useEffect(() => {
    if (
      cameraType === CameraTypes.VDG_RTSP ||
      CameraTypes.HIKVISION_RTSP ||
      CameraTypes.DAHUA_RTSP ||
      CameraTypes.GENETEC_RTSP ||
      CameraTypes.BOSCH_RTSP
    )
      playerRef.current?.addEventListener('loadeddata', onHlsDataLoaded)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerRef.current])

  // WEBSOCKET
  const openWebsocketPlugin = useCallback(() => {
    const socket = new TypeSocket<string>('ws://localhost:3002/openWindow')
    socket.on('connected', () => {
      socket.send(streamPath)
    })
    const disconnectedListener = () =>
      notify('resources.cameras.dialogs.preview.WEBSOCKETPluginDisconnected')
    socket.on('disconnected', disconnectedListener)
    socket.on('rawMessage', (value) => {
      socket.off('disconnected', disconnectedListener)
      socket.disconnect()
      return value === 'OK'
        ? notify(
            'resources.cameras.dialogs.preview.WEBSOCKETPluginVideoOpening',
          )
        : notify(
            `${translate(
              'resources.cameras.dialogs.preview.WEBSOCKETPluginUnknownMessage',
            )} - ${value}`,
          )
    })
    socket.connect()
  }, [translate, notify, streamPath])
  useEffect(() => {
    if (
      open &&
      streamPath &&
      streamPath !== '' &&
      cameraType === CameraTypes.WEBSOCKET
    ) {
      openWebsocketPlugin()
      close()
    }
  }, [
    open,
    close,
    cameraType,
    notify,
    translate,
    openWebsocketPlugin,
    streamPath,
  ])

  const renderCameraView = () => {
    switch (cameraType) {
      case CameraTypes.VDG_RTSP:
      case CameraTypes.HIKVISION_RTSP:
      case CameraTypes.DAHUA_RTSP:
      case CameraTypes.GENETEC_RTSP:
      case CameraTypes.BOSCH_RTSP:
        return (
          <Dialog
            open={open}
            onClose={handleClose}
            PaperComponent={DraggableComponent}
            aria-labelledby="draggable-dialog-title"
            maxWidth="md"
          >
            <DialogActions
              className={classes.actions}
              style={{ cursor: 'move' }}
            >
              <Typography className={classes.title} id="draggable-dialog-title">
                {cameraDescription}
              </Typography>
              <IconButton
                size="small"
                className={classes.button}
                onClick={handleDownloadClip}
                disabled={mutationLoading}
              >
                <GetApp fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                className={classes.button}
                onClick={handleClose}
              >
                <Close fontSize="small" />
              </IconButton>
            </DialogActions>
            <DialogContent className={classes.dialogContent}>
              {showHLSPlayer && (
                <ReactHlsPlayer
                  className={classes.video}
                  playerRef={playerRef}
                  src={streamPath}
                  autoPlay
                  muted
                  controls
                  width="auto"
                  height="auto"
                />
              )}
            </DialogContent>
          </Dialog>
        )
      case CameraTypes.VDG_MJPEG:
      case CameraTypes.AXXON_MJPEG:
      case CameraTypes.WISENET_WAVE_MJPEG:
      case CameraTypes.AVIGILON_7:
        return open && streamPath ? (
          <Popout
            url={streamPath}
            title={cameraDescription}
            onClosing={handleClose}
          />
        ) : (
          <></>
        )
      default:
        return <></>
    }
  }

  useEffect(() => {
    if (open) {
      openStream()
    }
  }, [open, openStream])

  useEffect(() => {
    if (!open) {
      setHLSStreamLoaded(false)
      setStreamPath('')
      closeStream()
      setJobId(undefined)
      setMaxHLSRetriesLeft(30)
    }
  }, [open, closeStream])

  return renderCameraView()
}

export const OpenCameraPreviewDialogButton = ({
  label,
  startDate,
  cameraData,
  logId,
  props,
  showAllCamerasSelect,
}: {
  label?: string
  startDate?: DateTime
  cameraData?: CameraPreviewData
  logId?: number
  props?: ButtonProps
  showAllCamerasSelect?: boolean | true
}) => {
  const classes = useStyles()
  const [dialogOpen, setDialogOpen] = useState(false)
  const hasAuthority = useHasAuthority()
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [selectedCamera, setSelectedCamera] = useState<number>(0)
  const formApiRef = useRef<any>(null)
  const cameraSelectRef = useRef<any>(null)
  const [isCameraSelectOpen, setIsCameraSelectOpen] = useState(false)

  const onClose = () => {
    setDialogOpen(false)
    setSelectedCamera(0)
    setIsCameraSelectOpen(false)
  }

  const openDialog = () => {
    setDialogOpen(true)
    formApiRef.current?.change('cameraId', null)
    setIsCameraSelectOpen(false)
  }

  const handleOpenCameraSelect = () => {
    setIsCameraSelectOpen(true)
    formApiRef.current?.focus?.('cameraId')
  }
  const handleCloseCameraSelect = () => {
    setIsCameraSelectOpen(false)
  }

  return (
    <>
      {hasAuthority(Authority.CAMERAS_PREVIEW_BUTTON) && (
        <CameraPreviewDialog
          open={dialogOpen}
          close={onClose}
          startDate={startDate}
          cameraData={{ id: selectedCamera }}
          logId={logId}
        />
      )}
      <Button
        variant="contained"
        color="inherit"
        className={classes.button}
        label={
          label == null ? 'resources.cameras.actions.openCameraPreview' : label
        }
        useSmallVersionBreakpoint={false}
        disabled={!hasAuthority(Authority.CAMERAS_PREVIEW_BUTTON)}
        {...props}
        onClick={() => {
          setSelectedCamera(cameraData?.id || 0)
          openDialog()
        }}
      >
        {props?.disabled ? <VideocamOff /> : <Videocam />}
      </Button>
      {showAllCamerasSelect && (
        <Button
          variant="contained"
          color="inherit"
          className={classes.button}
          label=""
          useSmallVersionBreakpoint={false}
          {...props}
          onClick={handleOpenCameraSelect}
        >
          {!hasAuthority(Authority.ALL_CAMERAS_PREVIEW_SELECT) ? undefined : (
            <SwitchVideo />
          )}
        </Button>
      )}

      {showAllCamerasSelect &&
        hasAuthority(Authority.ALL_CAMERAS_PREVIEW_SELECT) && (
          <Form onSubmit={() => undefined}>
            {({ form }) => {
              formApiRef.current = form
              return (
                <FilteredReferenceInput
                  label=""
                  /* removed: ref={cameraSelectRef} */
                  className={classes.cameraSelect}
                  source="cameraId"
                  defaultValue={null}
                  reference={ResourceName.CAMERAS}
                  sort={{ field: 'id', order: 'DESC' }}
                  perPage={smallScreen ? 5 : 15}
                  filterSource="search"
                  selectWithPaginationInputProps={{
                    optionText: 'description',
                    showFilter: true,
                    options: {
                      SelectProps: {
                        open: isCameraSelectOpen,
                        onClose: handleCloseCameraSelect,
                        onOpen: () => setIsCameraSelectOpen(true),
                        // optional: try to get the trigger if the inner component supports it
                        inputRef: (el: HTMLElement | null) => {
                          cameraSelectRef.current = el
                        },
                      },
                    },
                  }}
                  onChange={(eventOrValue: any) => {
                    setSelectedCamera(
                      Number(eventOrValue?.target?.value ?? eventOrValue) || 0,
                    )
                    setIsCameraSelectOpen(false)
                    openDialog()
                  }}
                  {...props}
                />
              )
            }}
          </Form>
        )}
    </>
  )
}

export interface CameraPreviewData {
  readonly id: number
}
